import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { NotificationCategory, UserNotificationPreferenceModel } from '../../models/user-notification-preference.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ValueChangedEvent } from 'devextreme/ui/slider';

@Component({
  selector: 'sp-user-settings-notification-tasks',
  standalone: false,
  templateUrl: './user-settings-notification-tasks.component.html',
  styleUrl: './user-settings-notification-tasks.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserSettingsNotificationTasksComponent implements OnInit, OnChanges {

  @Input() userPreferences: UserNotificationPreferenceModel;
  @Output() updatePreferences = new EventEmitter<UserNotificationPreferenceModel>();

  formGroup: FormGroup;

  localUserPreferences: UserNotificationPreferenceModel;

  constructor(private fb: FormBuilder) {
  }

  ngOnInit(): void {
    this.initForm();
    if (this.userPreferences) {
      this.localUserPreferences = this.userPreferences;
      this.updateFormValues();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.userPreferences?.currentValue) {
      this.localUserPreferences = changes.userPreferences.currentValue;
      this.updateFormValues();
    }
  }

  get isEmailDisabled(): boolean {
    return !this.userPreferences?.channelSetting?.email;
  }

  onTaskUpdatesSwitchChanged(e: ValueChangedEvent): void {
    this.handleSwitchChange(e, NotificationCategory.Task, 'taskUpdatesSwitch');
  }

  onTaskPhotosFilesSwitchChanged(e: ValueChangedEvent): void {
    this.handleSwitchChange(e, NotificationCategory.TaskMedia, 'taskPhotosFilesSwitch');
  }

  onTaskNotesSwitchChanged(e: ValueChangedEvent): void {
    this.handleSwitchChange(e, NotificationCategory.TaskNote, 'taskNotesSwitch');
  }

  private handleSwitchChange(e: ValueChangedEvent, category: NotificationCategory, controlName: string): void {
    if (!e.event || !this.localUserPreferences) {
      return;
    }

    // Prevent changes when email channel is disabled
    if (!this.localUserPreferences.channelSetting?.email) {
      this.updateFormValues();
      return;
    }

    const emailValue = this.formGroup.controls[controlName].value;
    const taskSetting = this.findOrCreateTaskSetting(category);
    
    if (taskSetting) {
      taskSetting.email = emailValue;
      this.updatePreferences.emit(this.localUserPreferences); // <-- add this back
    }
  }

  private findOrCreateTaskSetting(category: NotificationCategory) {
    if (!this.localUserPreferences.taskSettings) {
      this.localUserPreferences.taskSettings = [];
    }

    let taskSetting = this.localUserPreferences.taskSettings.find(
      x => x.notificationCategoryId === category
    );

    if (!taskSetting) {
      taskSetting = {
        notificationCategoryId: category,
        email: false
      };
      this.localUserPreferences.taskSettings.push(taskSetting);
    }

    return taskSetting;
  }

  private updateFormValues(): void {
    if (!this.formGroup || !this.localUserPreferences) {
      return;
    }

    const controls = this.formGroup.controls;
    const emailDisabled = !this.localUserPreferences.channelSetting?.email;

    const getEmailValue = (category: NotificationCategory): boolean => {
      if (emailDisabled) {
        return false;
      }
      const setting = this.localUserPreferences.taskSettings?.find(
        x => x.notificationCategoryId === category
      );
      return setting?.email ?? false;
    };

    controls['taskUpdatesSwitch'].setValue(
      getEmailValue(NotificationCategory.Task),
      { emitEvent: false }
    );
    controls['taskPhotosFilesSwitch'].setValue(
      getEmailValue(NotificationCategory.TaskMedia),
      { emitEvent: false }
    );
    controls['taskNotesSwitch'].setValue(
      getEmailValue(NotificationCategory.TaskNote),
      { emitEvent: false }
    );
  }

  private initForm(): void {
    this.formGroup = this.fb.group({
      taskUpdatesSwitch: [''],
      taskPhotosFilesSwitch: [''],
      taskNotesSwitch: [''],
    });
  }
}
